/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import { type YmmeAcesVehicleResponse, type YmmeAcesVehicleData } from '../interface';
import { useCallback } from 'react';
import { vehicleSort } from '../utils/vehicleSort';
import { requestBaseURL } from '@/config/serviceAPI';
import { type AxiosInstance } from 'axios';
import { useAcesFlags } from '../utils/acesFlags';
import { useQueryResultOnSuccess } from '@/hooks/query/useQueryResultOnSuccess';

type Props = {
  includeRepairGuides?: boolean;
};

export const getACESVehicles = async (options?: Props, axiosInstance?: AxiosInstance) => {
  const { includeRepairGuides } = options ?? {};

  const response = await getAxios(axiosInstance).get<YmmeAcesVehicleResponse>(
    `${requestBaseURL}/ecomm/b2c/v2/currentUser/vehicles`,
    {
      params: { includeRepairGuides },
    },
  );
  return selector(response.data);
};

export const getKey = () => ['user', 'vehiclesAces'];

export const getFullKey = (options?: Props) => {
  return options ? [...getKey(), options] : getKey();
};

export const selector = (data: YmmeAcesVehicleResponse): YmmeAcesVehicleData => {
  return {
    vehiclesInfo: vehicleSort(data.vehiclesInfo),
  };
};

type Options = Props & {
  onSuccess?: () => void;
  enabled?: boolean;
};

export const useACESVehicles = ({
  onSuccess,
  enabled = false,
  includeRepairGuides,
}: Options = {}) => {
  const { acesEnabled } = useAcesFlags();

  const props = includeRepairGuides ? { includeRepairGuides } : undefined;

  const queryResult = useQuery({
    enabled: !!acesEnabled && enabled,
    queryKey: getFullKey(props),
    queryFn: () => getACESVehicles(props),
  });

  useQueryResultOnSuccess(queryResult, onSuccess, true);

  return queryResult;
};

const staleTime = 30 * 60000; // 30 minutes

type SingleVehicleProps = {
  vehicleId: string;
  enabled?: boolean;
  includeRepairGuides?: boolean;
};

export const useACESVehicle = (options: SingleVehicleProps) => {
  const { acesEnabled } = useAcesFlags();
  const { vehicleId, enabled, includeRepairGuides } = options;
  const props = includeRepairGuides ? { includeRepairGuides } : undefined;

  const query = useQuery({
    enabled: !!vehicleId && !!acesEnabled && enabled,
    queryKey: getFullKey(props),
    queryFn: () => getACESVehicles(props),
    select: useCallback(
      (vehicles: YmmeAcesVehicleResponse) =>
        vehicles.vehiclesInfo.find((vehicle) => vehicle.vehicleId === vehicleId),
      [vehicleId],
    ),
    staleTime,
  });

  return query;
};

export const prefetchACESVehiclesData = (
  queryClient: QueryClient,
  axiosInstance: AxiosInstance,
  options?: Props,
) => {
  return queryClient.prefetchQuery({
    queryKey: getFullKey(options),
    queryFn: () => getACESVehicles(options, axiosInstance),
  });
};
